import { AbstractControl, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewChecked, ChangeDetectorRef, Component, ComponentRef, IterableDiffers, OnInit, ViewChild, ViewContainerRef
} from '@angular/core';

import { isExternalLink } from '../../utils/util';
import { ThfComponentInjectorService } from '@totvs/thf-ui';

import { ThfModalPasswordRecoveryComponent } from '../thf-modal-password-recovery/thf-modal-password-recovery.component';
import { ThfModalPasswordRecoveryType } from '../thf-modal-password-recovery/enums/thf-modal-password-recovery-type.enum';
import { ThfPageLoginBaseComponent } from './thf-page-login-base.component';
import { ThfPageLoginRecovery } from './interfaces/thf-page-login-recovery.interface';
import { ThfPageLoginService } from './thf-page-login.service';

/**
 * @docsExtends ThfPageLoginBaseComponent
 *
 * @example
 *
 * <example name="thf-page-login-basic" title="Totvs Page Login Basic">
 *  <file name="sample-thf-page-login-basic/sample-thf-page-login-basic.component.html"> </file>
 *  <file name="sample-thf-page-login-basic/sample-thf-page-login-basic.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-login-labs" title="Totvs Page Login Labs">
 *  <file name="sample-thf-page-login-labs/sample-thf-page-login-labs.component.html"> </file>
 *  <file name="sample-thf-page-login-labs/sample-thf-page-login-labs.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-login-human-resources" title="Totvs Page Login - Human Resources">
 *  <file name="sample-thf-page-login-human-resources/sample-thf-page-login-human-resources.component.html"> </file>
 *  <file name="sample-thf-page-login-human-resources/sample-thf-page-login-human-resources.component.ts"> </file>
 *  <file name="sample-thf-page-login-human-resources/sample-thf-page-login-human-resources.module.ts"> </file>
 * </example>
 *
 * <example name="thf-page-login-automatic-service" title="Totvs Page Login - Automatic Service">
 *  <file name="sample-thf-page-login-automatic-service/sample-thf-page-login-automatic-service.component.html"> </file>
 *  <file name="sample-thf-page-login-automatic-service/sample-thf-page-login-automatic-service.component.ts"> </file>
 * </example>
 */

@Component({
  selector: 'thf-page-login',
  templateUrl: './thf-page-login.component.html'
})

export class ThfPageLoginComponent extends ThfPageLoginBaseComponent implements AfterViewChecked, OnInit {

  private componentRef: ComponentRef<any> = null;
  private differ: any;
  private readonly customPasswordError = { custom : false };

  @ViewChild('loginForm', { read: NgForm, static: true }) loginForm: NgForm;
  @ViewChild('pageLogin', { read: ViewContainerRef, static: true }) pageLogin: ViewContainerRef;

  constructor(
    public changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private thfComponentInjector: ThfComponentInjectorService,
    differs: IterableDiffers,
    loginService: ThfPageLoginService,
    router: Router,
    ) {
    super(loginService, router);
    this.differ = differs.find([]).create(null);
  }

  ngAfterViewChecked() {
    if (this.differ) {
      this.validateArrayChanges(this.differ, [
        { array: this.loginErrors, callback: this.generateLoginError.bind(this) },
        { array: this.passwordErrors, callback: this.generatePasswordError.bind(this) }
      ]);
    }
  }

  ngOnInit() {
    this.checkingForRouteMetadata(this.activatedRoute.snapshot.data);
  }

  changeLoginModel() {
    if (this.authenticationUrl) {
      this.loginErrors = [];
    } else {
      this.setLoginErrors(this.loginErrors);
      this.loginChange.emit(this.login);
    }
  }

  changePasswordModel() {
    if (this.authenticationUrl) {
      this.passwordErrors = [];
    } else {
      this.setPasswordErrors(this.passwordErrors);
      this.passwordChange.emit(this.password);
    }
  }

  private checkingForMetadataProperty(object, property) {
    if (Object.prototype.hasOwnProperty.call(object, property)) {
      return object[property];
    }
  }

  private checkingForRouteMetadata(data) {
    if (Object.keys(data).length !== 0) {
      this.authenticationUrl = this.checkingForMetadataProperty(data, 'serviceApi') || this.authenticationUrl;
      this.authenticationType = this.checkingForMetadataProperty(data, 'authenticationType') || this.authenticationType;
      this.environment = this.checkingForMetadataProperty(data, 'environment') || this.environment;
      this.recovery = this.checkingForMetadataProperty(data, 'recovery') || this.recovery;
      this.registerUrl = this.checkingForMetadataProperty(data, 'registerUrl') || this.registerUrl;
    }
  }

  private createModalPasswordRecoveryComponent(thfPageLoginRecovery: ThfPageLoginRecovery) {
    if (this.componentRef) {
      this.thfComponentInjector.destroyComponentInApplication(this.componentRef);
    }

    this.componentRef = this.thfComponentInjector.createComponentInApplication(ThfModalPasswordRecoveryComponent);
    this.componentRef.instance.urlRecovery = thfPageLoginRecovery.url;
    this.componentRef.instance.contactEmail = thfPageLoginRecovery.contactMail;
    this.componentRef.instance.phoneMask = thfPageLoginRecovery.phoneMask;
    this.componentRef.instance.type = thfPageLoginRecovery.type || ThfModalPasswordRecoveryType.Email;
    this.componentRef.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.componentRef.instance.open();
    });
  }

  private generateLoginError() {
    if (this.loginErrors && this.loginErrors.length) {
      this.setLoginErrors(this.loginErrors);
    } else {
      const control = this.loginForm.form.controls['login'];
      if (control) {
        this.resetControl(control);
      }
    }
  }

  private generatePasswordError() {
    if (this.passwordErrors && this.passwordErrors.length) {
      this.setPasswordErrors(this.passwordErrors);
    } else {
      const control = this.loginForm.form.controls['password'];

      if (control) {
        this.resetControl(control);
      }
    }
  }

  private resetControl(control: AbstractControl) {
    control.markAsPristine();
    control.markAsUntouched();
    control.updateValueAndValidity();
  }

  private setControlErrors(allErrors: string, control: AbstractControl, errors: Array<string>, patternError: string) {

    if (control) {

      this[allErrors] = control.hasError('pattern') ? [...errors, ...[patternError]] : [...errors];

      if (errors && errors.length && (control.valid || control.pristine)) {
        control.markAsTouched();
        control.markAsDirty();
        control.setErrors(this.customPasswordError);
      }
    }

  }

  private setUrlRedirect(url) {
    isExternalLink(url) ?  window.open(url, '_blank') : this.router.navigate([url]);
  }

  private validateArrayChanges(differ: any, array: Array<{ array: Array<any>, callback: any }>) {

    array.forEach(element => {
      const changes = differ.diff(element.array);
      if (changes) {
        element.callback();
        this.changeDetector.detectChanges();
      }
    });
  }

  protected openUrl(recovery: any): void {
    switch (typeof recovery) {
      case 'string': {
        this.setUrlRedirect(recovery);
        break;
      }
      case 'function': {
        recovery();
        break;
      }
      case 'object': {
        this.createModalPasswordRecoveryComponent(recovery);
        break;
      }
    }
  }

  protected setLoginErrors(errors: Array<string>) {
    const control = this.loginForm.form.controls['login'];
    this.setControlErrors('allLoginErrors', control, errors, this.literals.loginErrorPattern);
  }

  protected setPasswordErrors(errors: Array<string>) {
    const control = this.loginForm.form.controls['password'];
    this.setControlErrors('allPasswordErrors', control, errors, this.literals.passwordErrorPattern);
  }

  onSelectedLanguage(language: string) {
    this.selectedLanguage = language;
    this.getLiterals(language, this.containsCustomLiterals ? this.customizedDefaultLiterals : undefined);
    this.setTitleLiteral(language, this.productName);
    this.setLoginHintLiteral(language, this.contactEmail);
  }

}
